import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";

import {
  CoverageBoolean,
  CoverageDebrisRemoval,
  CoverageEarthquake,
  CoverageEquipmentBreakdown,
  CoverageGolfCart,
  CoverageIdentityFraudExpense,
  CoverageInflationGuard,
  CoverageLossAssessment,
  CoverageResidenceBurglary,
  CoverageMineSubsidenceScheduled,
  CoverageScheduledPersonalProperty,
  CoverageSecondaryResidenceLiability,
  CoverageWaterBackupAndSumpOverflow,
  ExtraCoverageKeys,
} from "@covertree/new-business-graphql";
import { FormToggleCard, HelpText } from "@covertree/ui";
import { formatCurrency } from "@covertree/utils";

import { ExtraCoveragesForm } from "../validatorsAndTypes/extraCoveragesForm";

import { SecondaryResidenceLiabilityCard } from "./SecondaryResidenceLiabilityCard";
import { ScheduledPersonalPropertyCard } from "./SchedulesPersonalProperty";
import { MineSubsidenceScheduledCard } from "./MineSubsidenceScheduled/MineSubsidenceScheduledCard";
import { colors, theme } from "@covertree/theme";
import {
  ExtraCoverageAccessor,
  getExtraCoveragesMap,
  getMineSubsidenceOption,
  getMineSubsidenceScheduledOption,
  getRoofPaymentScheduleOption,
  ScreenWithCoverages,
} from "@covertree/new-business-mappings";
import {
  selectPolicy,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";

export interface ExtraCoverageCardProps {
  extraCoverage: Omit<
    | CoverageBoolean
    | CoverageDebrisRemoval
    | CoverageEarthquake
    | CoverageEquipmentBreakdown
    | CoverageIdentityFraudExpense
    | CoverageInflationGuard
    | CoverageLossAssessment
    | CoverageGolfCart
    | CoverageResidenceBurglary
    | CoverageMineSubsidenceScheduled
    | CoverageSecondaryResidenceLiability
    | CoverageWaterBackupAndSumpOverflow
    | CoverageScheduledPersonalProperty,
    "valueIsHidden"
  >;
  extraCoverageAccessor: ExtraCoverageAccessor;
  extraCoverageKey: ExtraCoverageKeys;
  monthlyPrice: number;
  yearlyPrice: number;
}

export function ExtraCoverageCard({
  extraCoverage,
  extraCoverageAccessor,
  extraCoverageKey,
  monthlyPrice,
  yearlyPrice,
}: ExtraCoverageCardProps) {
  const { control } = useFormContext<ExtraCoveragesForm>();
  const { units } = useNewBusinessSelector(selectPolicy)!;

  const coverageOptions = useMemo(
    () => ({
      mineSubsidenceOption: getMineSubsidenceOption(units[0]),
      mineSubsidenceScheduledOption: getMineSubsidenceScheduledOption(units[0]),
      roofPaymentScheduleOption: getRoofPaymentScheduleOption(units[0]),
    }),
    [units]
  );

  const extraCoverageMap = getExtraCoveragesMap(
    ScreenWithCoverages.ExtraCoverages
  );

  const { getName, image, getDescription, getTooltip, getValueFormats } =
    useMemo(
      () => extraCoverageMap[extraCoverageAccessor],
      [extraCoverageAccessor]
    );

  const { name, valueFormats, tooltip, description } = useMemo(
    () => ({
      name: getName(coverageOptions),
      valueFormats:
        "defaultValue" in extraCoverage
          ? getValueFormats(extraCoverage.defaultValue as never)
          : undefined,
      tooltip: getTooltip(coverageOptions) || "",
      description: getDescription(coverageOptions) || "",
    }),
    [extraCoverage, coverageOptions]
  );

  // If any Extra Coverage needs custom behaviour, we are creating separate component for it

  if (
    extraCoverageAccessor === ExtraCoverageAccessor.SecondaryResidenceLiability
  ) {
    return (
      <SecondaryResidenceLiabilityCard
        secondaryResidenceLiabilityDetails={{
          description,
          image,
          name,
          monthlyPrice,
          yearlyPrice,
          tooltip,
          valueIsCustomizable: extraCoverage.valueIsCustomizable,
        }}
      />
    );
  }

  if (extraCoverageAccessor === ExtraCoverageAccessor.ValuableItemProtection) {
    return (
      <ScheduledPersonalPropertyCard
        scheduledPersonalPropertyDetails={{
          description,
          image,
          name,
          tooltip,
          valueIsCustomizable: extraCoverage.valueIsCustomizable,
        }}
      />
    );
  }

  if (extraCoverageAccessor === ExtraCoverageAccessor.MineSubsidenceScheduled) {
    return (
      <MineSubsidenceScheduledCard
        description={description}
        image={image}
        name={name}
        tooltip={tooltip}
        valueIsCustomizable={extraCoverage.valueIsCustomizable}
      />
    );
  }

  return (
    <Controller
      control={control}
      name={extraCoverageKey}
      render={({ field }) => (
        <FormToggleCard
          title={name}
          image={image}
          tooltip={tooltip}
          actionText={
            monthlyPrice > 0
              ? `+ ${formatCurrency(monthlyPrice, 2)}/mo`
              : undefined
          }
          actionSubText={
            yearlyPrice > 0
              ? `(${formatCurrency(yearlyPrice, 2)}/year)`
              : undefined
          }
          subheader={description}
          subheaderVariant="grey"
          onToggle={() => {
            field.onChange(!field.value);
          }}
          checked={
            Array.isArray(field.value) ? field.value.length > 0 : !!field.value
          }
          checkedCustomStyles={{
            backgroundColor: theme.palette.secondary.contrastText,
          }}
          actions={
            valueFormats?.value &&
            valueFormats?.prefix &&
            "defaultValue" in extraCoverage ? (
              <HelpText
                text={valueFormats.prefix}
                spanText={String(valueFormats.value)}
                valueColor={colors.brown}
              />
            ) : undefined
          }
          customizable={extraCoverage.valueIsCustomizable}
        />
      )}
    />
  );
}
